import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { isNil } from 'lodash';
import { PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models';
import CustomDnsBinding from 'mbs-ui-shared@models/CustomDnsBinding';
import CustomDnsCertificate from 'mbs-ui-shared@models/CustomDnsCertificate';
import { CustomDnsBindingsService } from 'mbs-ui-shared@services/custom-dns-bindings.service';
import { CustomDnsCertificatesService } from 'mbs-ui-shared@services/custom-dns-certificates.service';
import { withPagingAndData } from 'mbs-ui-shared@store/withPagingAndDataStoreFeature/withPagingAndData';
import { Observable, pipe } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { CustomDnsBindingStoreProperties } from './customDnsBindingStore.types';

// ---------------------------------------------------------------------------
// Custom feature for certificates
// ---------------------------------------------------------------------------

function withCertificatesFeature() {
  return signalStoreFeature(
    withState<CustomDnsBindingStoreProperties>({
      certificates: [],
      loadedCertificates: false,
      loadingCertificates: false
    }),
    withComputed((store) => ({
      selectCertificates: computed(() => store.certificates()),
      selectLoadedCertificates: computed(() => store.loadedCertificates()),
      selectLoadingCertificates: computed(() => store.loadingCertificates())
    })),
    withMethods((store, service = inject(CustomDnsCertificatesService)) => ({
      loadCertificates: rxMethod<{ force?: boolean } | void>(
        pipe(
          filter((params) => !!(params as { force?: boolean })?.force || !store.loadedCertificates()),
          mergeMap(() =>
            service.get().pipe(
              tapResponse({
                next: (result: { data: CustomDnsCertificate[] }) =>
                  patchState(store, {
                    certificates: result?.data ?? [],
                    loadedCertificates: !isNil(result?.data),
                    loadingCertificates: false
                  }),
                error: () => patchState(store, { certificates: [], loadedCertificates: false, loadingCertificates: false })
              })
            )
          )
        )
      )
    }))
  );
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const CustomDnsBindingStore = signalStore(
  { providedIn: 'root' },
  withDevtools('CustomDnsBindingStore'),
  withPagingAndData<CustomDnsBinding>({
    selectId: (entity) => entity?.id,
    getEntityById: () => {
      const service = inject(CustomDnsBindingsService);
      return (id: string): Observable<CustomDnsBinding> => service.getById(id);
    },
    getEntitiesByIds: () => null,
    getEntities: () => {
      const service = inject(CustomDnsBindingsService);
      return (params: QueryFiltersParams & QueryPagingParams): Observable<PagedResponse<CustomDnsBinding>> => service.get(params);
    }
  }),
  withCertificatesFeature()
);

export type CustomDnsBindingStore = InstanceType<typeof CustomDnsBindingStore>;
